import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { SkeletonDetailPage } from "../components/SkeletonLoader";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import FadeIn from "../components/FadeIn";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Edit2, Trash2, Package, Copy, Check, ChevronRight, X, Search, Loader2, FileText, Upload, Download, Pencil, File, CarFront, AlertTriangle, Sparkles, Wand2 } from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";
import Modal from "../components/Modal";
import ContactPersonList from "../components/ContactPersonList";
import CustomerSites from "../components/CustomerSites";
import AiSummaryCard from "../components/ai/AiSummaryCard";
import AiDocumentIntake from "../components/ai/AiDocumentIntake";
import AiPdfEditor from "../components/ai/AiPdfEditor";

const isPdfName = (name) => /\.pdf$/i.test(name || "");
import { MACHINE_TYPES, MACHINE_STATUSES, MACHINE_STATUS_LABELS, MACHINE_STATUS_COLORS, DEFAULT_MACHINE_STATUS, DEFAULT_MACHINE_TYPE } from '../constants';

// HistoryTab - zobrazí ALL audit záznamy k zákazníkovi (zákazník + dokumenty + stroje)
const HistoryTab = ({ customerId }) => {
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/customers/${customerId}/audit-logs`, { credentials: "include" })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setAuditLog(data.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to load audit log:", err);
        setError(err.message);
        setLoading(false);
      });
  }, [customerId]);

  const actionColors = {
    create: { bg: "#E8F5E9", color: "#388E3C", border: "#A5D6A7" },
    update: { bg: "#E3F2FD", color: "#1976D2", border: "#90CAF9" },
    delete: { bg: "#FFEBEE", color: "#E53935", border: "#FFCDD2" },
  };

  const actionLabels = { create: "Vytvořeno", update: "Upraveno", delete: "Smazáno" };

  const entityLabels = {
    customer: "Zákazník",
    customer_document: "Dokument",
    machine: "Trenažér",
    machine_document: "Dok. trenažéru",
    contact_person: "Kontakt",
  };

  const formatChanges = (entry) => {
    const c = entry.changes;
    if (!c || typeof c !== "object") return null;
    const { entity_type, action } = entry;

    // Dokument
    if (entity_type === "customer_document") {
      if (action === "create") return `Vytvořen dokument „${c.document_name || ""}" (${c.file_count || 0} souborů)`;
      if (action === "update") return `Přejmenován: „${c.before || ""}" → „${c.after || ""}"`;
      if (action === "delete") return `Smazán dokument „${c.document_name || ""}" (${c.file_count || 0} souborů)`;
    }

    // Trenažér
    if (entity_type === "machine") {
      if (action === "create") return `Přidán trenažér ${c.model_type || ""} (SN: ${c.serial_number || "—"})`;
      if (action === "delete") return `Smazán trenažér ${c.model_type || ""} (SN: ${c.serial_number || "—"})`;
      if (action === "update") {
        if (c.new && c.old) {
          const machineFieldLabels = { status: "Status", notes: "Poznámky", model_type: "Typ", serial_number: "SN", serial_number_final: "SN final", contract_signed_at: "Smlouva", delivered_at: "Doručeno" };
          const changed = Object.keys(c.new).filter(k => {
            if (k === "updated_at") return false;
            const ov = c.old[k]; const nv = c.new[k];
            if (typeof ov === "object" || typeof nv === "object") return false;
            return String(ov || "").trim() !== String(nv || "").trim();
          });
          if (changed.length === 0) return "Upraven trenažér";
          return "Upraven trenažér: " + changed.map(k => `${machineFieldLabels[k] || k}: „${c.old[k] || "—"}" → „${c.new[k] || "—"}"`).join(", ");
        }
      }
    }

    // Zákazník
    if (entity_type === "customer") {
      if (action === "create") return `Zákazník vytvořen`;
      if (action === "delete") return `Zákazník smazán`;
      if (action === "update" && c.new && c.old) {
        const fieldLabels = { company_name: "Název", ico: "IČO", dic: "DIČ", email: "Email", phone: "Tel.", city: "Město", street: "Ulice", postal_code: "PSČ", location: "Místo autoškoly", proxy_name: "Zastupitel", notes: "Poznámky" };
        const changed = Object.keys(c.new).filter(k => {
          const ov = c.old[k]; const nv = c.new[k];
          if (typeof ov === "object" || typeof nv === "object") return false;
          return String(ov || "").trim() !== String(nv || "").trim() && !!fieldLabels[k];
        });
        if (changed.length === 0) return "Zákazník upraven";
        return "Upraveno: " + changed.map(k => `${fieldLabels[k]}: „${c.old[k] || ""}" → „${c.new[k] || ""}"`).join(", ");
      }
    }

    // Kontaktní osoba
    if (entity_type === "contact_person") {
      if (action === "create") return `Přidán kontakt: ${c.name || ""}`;
      if (action === "delete") return `Smazán kontakt: ${c.name || ""}`;
      if (action === "update") return `Upraven kontakt`;
    }

    // Fallback
    const summary = JSON.stringify(c);
    return summary.length > 120 ? summary.substring(0, 120) + "…" : summary;
  };

  if (loading) return <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>Načítání...</div>;
  if (error) return <div style={{ color: "#E53935", padding: "16px" }}>Chyba načítání: {error}</div>;
  if (auditLog.length === 0) return (
    <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
      <FileText size={32} style={{ opacity: 0.4, marginBottom: "12px" }} />
      <p>Žádná historie změn</p>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {auditLog.map((entry) => {
        const ac = actionColors[entry.action] || { bg: "#F5F5F5", color: "#666", border: "#E0E0E0" };
        const date = new Date(entry.created_at).toLocaleString("cs-CZ");
        const user = entry.username || "systém";
        const entityLabel = entityLabels[entry.entity_type] || entry.entity_type;
        const actionLabel = actionLabels[entry.action] || entry.action;
        const detail = formatChanges(entry);
        return (
          <div key={entry.id} style={{ padding: "12px 14px", backgroundColor: "#F9F9F9", borderRadius: "10px", border: "1px solid #E0E0E0", borderLeft: `4px solid ${ac.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "12px", padding: "2px 8px", borderRadius: "12px", backgroundColor: ac.bg, color: ac.color, fontWeight: 600, whiteSpace: "nowrap" }}>{actionLabel}</span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>{entityLabel}</span>
                <span style={{ fontSize: "13px", color: "#888" }}>— {user}</span>
              </div>
              <span style={{ fontSize: "12px", color: "#AAA", whiteSpace: "nowrap" }}>{date}</span>
            </div>
            {detail && (
              <p style={{ fontSize: "13px", color: "#555", margin: "6px 0 0", lineHeight: 1.5 }}>{detail}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const pendingContactsRef = useRef([]);
  const [customer, setCustomer] = useState(null);
  const [machines, setMachines] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useDelayedLoading(loading, 250);
  const [showMachineForm, setShowMachineForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(null);
  const [copyToast, setCopyToast] = useState(false);
  const [aresLoading, setAresLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [aresError, setAresError] = useState("");
  const [activeTab, setActiveTab] = useState("info");
  const [user, setUser] = useState(null);
  const [showAiIntake, setShowAiIntake] = useState(false);
  const [pdfEditSource, setPdfEditSource] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [machineForm, setMachineForm] = useState({ model_type: DEFAULT_MACHINE_TYPE, contract_signed_at: today, status: DEFAULT_MACHINE_STATUS });

  // Document states (v2)
  const [showCreateDocModal, setShowCreateDocModal] = useState(false);
  const [showDocEditModal, setShowDocEditModal] = useState(false);
  const [docToEdit, setDocToEdit] = useState(null);
  const [editDocName, setEditDocName] = useState("");
  const [editDocFiles, setEditDocFiles] = useState([]); // existing files in document
  const [editUploadFiles, setEditUploadFiles] = useState([]); // new files to upload in edit modal
  const [editUploading, setEditUploading] = useState(false);
  const [createDocName, setCreateDocName] = useState("");
  const [createDocFiles, setCreateDocFiles] = useState([]);
  const [createUploading, setCreateUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const statusLabels = MACHINE_STATUS_LABELS;
  const statusColors = MACHINE_STATUS_COLORS;

  useEffect(() => { loadData(); fetchUser(); }, [id]);

  const fetchUser = async () => {
    try {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      if (res.ok) {
        const json = await res.json();
        setUser(json.user || json.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadData = async () => { setLoading(true); await Promise.all([fetchCustomer(), fetchMachines(), fetchDocuments()]); setLoading(false); };
  const fetchCustomer = async () => { const res = await fetch("/api/customers/" + id, { credentials: "include" }); if (res.ok) { const json = await res.json(); setCustomer(json.data || json); } };
  const fetchMachines = async () => { const res = await fetch("/api/customers/" + id + "/machines", { credentials: "include" }); if (res.ok) { const json = await res.json(); setMachines(json.data || []); } };
  const fetchDocuments = async () => { 
    const res = await fetch("/api/customers/" + id + "/documents", { credentials: "include" }); 
    if (res.ok) { 
      const json = await res.json(); 
      setDocuments(json.data || []); 
    } 
  };

  const copyToClipboard = (text, tid) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(tid);
      setCopyToast(true);
      setTimeout(() => setCopied(null), 1500);
      setTimeout(() => setCopyToast(false), 2000);
    }).catch(err => console.error('Copy failed:', err));
  };
  const handleAddMachine = async (e) => { e.preventDefault(); const res = await fetch("/api/customers/" + id + "/machines", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(machineForm) }); if (res.ok) { setShowMachineForm(false); fetchMachines(); } };
  const handleDeleteMachine = async (e, mid) => { e.preventDefault(); e.stopPropagation(); setConfirmAction({ title: "Smazat trenažér?", message: "Opravdu chcete smazat tento trenažér? Tato akce je nevratná.", danger: true, onConfirm: async () => { await fetch("/api/machines/" + mid, { method: "DELETE", credentials: "include" }); fetchMachines(); setConfirmAction(null); } }); };
  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    const res = await fetch("/api/customers/" + id, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(customer) });
    if (res.ok) {
      // Uložit pending (nové) kontakty do DB
      for (const contact of pendingContactsRef.current) {
        const { _pending, _tempId, ...contactData } = contact;
        await fetch(`/api/customers/${id}/contacts`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(contactData) });
      }
      pendingContactsRef.current = [];
      setEditing(false);
      fetchCustomer();
    }
  };
  const handleDeleteCustomer = async () => { setConfirmAction({ title: "Přesunout zákazníka do koše?", message: "Zákazník zmizí ze seznamů, mapy i přehledů. Jeho trenažéry a dokumenty zůstanou zachované a zákazníka lze obnovit v Administraci → Koš.", danger: true, onConfirm: async () => { const res = await fetch("/api/customers/" + id, { method: "DELETE", credentials: "include" }); setConfirmAction(null); if (res.ok) navigate("/customers"); else { const j = await res.json().catch(() => ({})); window.alert(j.error || "Zákazníka se nepodařilo smazat"); } } }); };

  // Document handlers (v2)

  // Create new document (with optional files)
  const handleCreateDocument = async () => {
    if (!createDocName.trim()) return;
    setCreateUploading(true);
    try {
      const formData = new FormData();
      formData.append("name", createDocName.trim());
      createDocFiles.forEach(f => formData.append("files", f.file));

      const res = await fetch("/api/customers/" + id + "/documents", {
        method: "POST",
        credentials: "include",
        body: formData
      });
      if (res.ok) {
        setShowCreateDocModal(false);
        setCreateDocName("");
        setCreateDocFiles([]);
        fetchDocuments();
      } else {
        const json = await res.json();
        alert(json.error || "Nepodařilo se vytvořit dokument");
      }
    } catch (err) {
      console.error(err);
      alert("Chyba při vytváření dokumentu");
    }
    setCreateUploading(false);
  };

  // Open edit modal for a document
  const openDocEditModal = (doc) => {
    setDocToEdit(doc);
    setEditDocName(doc.name);
    setEditDocFiles(doc.files || []);
    setEditUploadFiles([]);
    setShowDocEditModal(true);
  };

  // Save document edit (rename + upload new files)
  const handleSaveDocEdit = async () => {
    if (!docToEdit || !editDocName.trim()) return;
    setEditUploading(true);
    try {
      // 1. Rename document
      const renameRes = await fetch("/api/customers/" + id + "/documents/" + docToEdit.id, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: editDocName.trim() })
      });
      if (!renameRes.ok) {
        alert("Nepodařilo se přejmenovat dokument");
        setEditUploading(false);
        return;
      }

      // 2. Upload new files (if any)
      if (editUploadFiles.length > 0) {
        const formData = new FormData();
        editUploadFiles.forEach(f => formData.append("files", f.file));
        const uploadRes = await fetch("/api/customers/" + id + "/documents/" + docToEdit.id + "/files", {
          method: "POST",
          credentials: "include",
          body: formData
        });
        if (!uploadRes.ok) {
          alert("Dokument přejmenován, ale nepodařilo se nahrát soubory");
        }
      }

      setShowDocEditModal(false);
      setDocToEdit(null);
      setEditDocName("");
      setEditDocFiles([]);
      setEditUploadFiles([]);
      fetchDocuments();
    } catch (err) {
      console.error(err);
      alert("Chyba při ukládání");
    }
    setEditUploading(false);
  };

  // Delete entire document
  const openDeleteDocConfirm = (doc) => {
    setConfirmAction({
      title: "Smazat dokument?",
      message: `Smazat dokument "${doc.name}" včetně všech souborů (${(doc.files || []).length})? Tato akce je nevratná.`,
      danger: true,
      onConfirm: async () => {
        try {
          const res = await fetch("/api/customers/" + id + "/documents/" + doc.id, {
            method: "DELETE",
            credentials: "include"
          });
          if (res.ok) {
            setConfirmAction(null);
            setShowDocEditModal(false);
            setDocToEdit(null);
            fetchDocuments();
          }
        } catch (err) {
          console.error(err);
        }
      }
    });
  };

  // Delete single file from document
  const handleDeleteFile = (docId, fileId, fileName) => {
    setConfirmAction({
      title: "Smazat soubor?",
      message: `Smazat soubor "${fileName}"?`,
      danger: true,
      onConfirm: async () => {
        try {
          const res = await fetch("/api/customers/" + id + "/documents/" + docId + "/files/" + fileId, {
            method: "DELETE",
            credentials: "include"
          });
          if (res.ok) {
            setConfirmAction(null);
            // Update local state immediately
            setEditDocFiles(prev => prev.filter(f => f.id !== fileId));
            fetchDocuments();
          }
        } catch (err) {
          console.error(err);
        }
      }
    });
  };

  // Download a file
  const downloadFile = async (docId, fileId, fileName) => {
    try {
      const response = await fetch(`/api/customers/${id}/documents/${docId}/files/${fileId}/download`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('Chyba při stahování souboru');
    }
  };


  // Helper functions for file display
  const getFileExtension = (filename) => {
    if (!filename) return '';
    return filename.split('.').pop().toLowerCase();
  };

  const getFileIcon = (ext) => {
    const icons = {
      pdf: '📕',
      doc: '📘', docx: '📘',
      xls: '📗', xlsx: '📗',
      txt: '📝',
      jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️', webp: '🖼️',
      zip: '📦', rar: '📦', '7z': '📦',
      mp4: '🎬', avi: '🎬', mov: '🎬',
      mp3: '🎵', wav: '🎵',
      ppt: '📙', pptx: '📙',
    };
    return icons[ext] || '📎';
  };

  // Add files to create modal
  const handleCreateFileSelect = (files) => {
    const newFiles = Array.from(files).map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9)
    }));
    setCreateDocFiles(prev => [...prev, ...newFiles]);
  };

  // Add files to edit modal
  const handleEditFileSelect = (files) => {
    const newFiles = Array.from(files).map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9)
    }));
    setEditUploadFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleCreateFileSelect(e.dataTransfer.files);
    }
  };

  const lookupAres = async () => {
    const ico = (customer.ico || "").replace(/\s/g, "");
    if (!ico || ico.length !== 8) { setAresError("IČO musí mít 8 číslic"); return; }
    setAresLoading(true);
    setAresError("");
    try {
      const res = await fetch("/api/customers/ares/" + ico, { credentials: "include" });
      const json = await res.json();
      if (res.ok && json.data) {
        setCustomer(prev => ({ ...prev, ...json.data }));
      } else {
        setAresError(json.message || "Subjekt nenalezen");
      }
    } catch (err) {
      setAresError("Nepodařilo se připojit k ARES");
    }
    setAresLoading(false);
  };

  if (showLoading) return <SkeletonDetailPage />;
  if (!customer) return <div style={{ padding: "32px", color: "#666" }}>Zákazník nenalezen</div>;

  const inputStyle = { width: "100%", padding: "10px 14px", border: "1px solid #E0E0E0", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: "13px", fontWeight: 500, color: "#333", marginBottom: "6px" };
  const canSeeHistory = user && (user.role === 'admin' || user.role === 'manager');

  return (
    <FadeIn show={!showLoading}>
    <div style={{ padding: "24px" }}>
      <button onClick={() => navigate("/customers")} style={{ display: "flex", alignItems: "center", gap: "8px", background: "none", border: "none", color: "#666", cursor: "pointer", marginBottom: "24px", fontSize: "14px" }}>
        <ArrowLeft size={20} /> Zpět na zákazníky
      </button>

      <div style={{ backgroundColor: "#FFF", borderRadius: "12px", border: "1px solid #E0E0E0", padding: "24px", marginBottom: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
          <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#333", margin: 0 }}>{customer.company_name}</h1>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={() => { setEditing(true); setAresError(""); }} style={{ padding: "8px", borderRadius: "8px", border: "none", backgroundColor: "#F5F5F5", cursor: "pointer" }}><Edit2 size={18} color="#666" /></button>
            {user?.role === "admin" && <button onClick={handleDeleteCustomer} aria-label="Přesunout do koše" title="Přesunout do koše" style={{ padding: "8px", borderRadius: "8px", border: "none", backgroundColor: "#FFEBEE", cursor: "pointer" }}><Trash2 size={18} color="#E53935" /></button>}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px" }}>
          <div>
            <p style={{ fontSize: "13px", color: "#999", margin: "0 0 6px" }}>Zastupitel</p>
            <p style={{ fontSize: "15px", color: customer.proxy_name ? "#333" : "#999", margin: 0 }}>{customer.proxy_name || "Jméno - doplnit"}</p>
          </div>
          <div>
            <p style={{ fontSize: "13px", color: "#999", margin: "0 0 6px" }}>Telefon</p>
            {customer.phone ? (
              <button onClick={() => copyToClipboard(customer.phone, "phone")} style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#333", cursor: "pointer", fontSize: "15px", padding: 0 }}>
                {customer.phone} {copied === "phone" ? <Check size={14} color="#4CAF50" /> : <Copy size={14} color="#999" />}
              </button>
            ) : <p style={{ fontSize: "15px", color: "#999", margin: 0 }}>Tel. číslo - doplnit</p>}
          </div>
          <div>
            <p style={{ fontSize: "13px", color: "#999", margin: "0 0 6px" }}>Email</p>
            {customer.email ? (
              <button onClick={() => copyToClipboard(customer.email, "email")} style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#333", cursor: "pointer", fontSize: "15px", padding: 0 }}>
                {customer.email} {copied === "email" ? <Check size={14} color="#4CAF50" /> : <Copy size={14} color="#999" />}
              </button>
            ) : <p style={{ fontSize: "15px", color: "#999", margin: 0 }}>Email - doplnit</p>}
          </div>
          <div>
            <p style={{ fontSize: "13px", color: "#999", margin: "0 0 6px" }}>Adresa</p>
            <p style={{ fontSize: "15px", color: (customer.street || customer.city || customer.postal_code) ? "#333" : "#999", margin: 0 }}>
              {[customer.street, customer.city, customer.postal_code].filter(Boolean).join(", ") || "Adresa - doplnit"}
            </p>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: "24px" }}>
        <AiSummaryCard kind="customer" id={Number(id)} />
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
        <button onClick={() => setActiveTab("info")} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", backgroundColor: activeTab === "info" ? "#E53935" : "#F5F5F5", color: activeTab === "info" ? "#FFF" : "#666", cursor: "pointer", fontWeight: 500, fontSize: "14px", display: "flex", alignItems: "center", gap: "6px", transition: "all 200ms ease" }}><CarFront size={16} /> Trenažéry</button>
        <button onClick={() => setActiveTab("documents")} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", backgroundColor: activeTab === "documents" ? "#E53935" : "#F5F5F5", color: activeTab === "documents" ? "#FFF" : "#666", cursor: "pointer", fontWeight: 500, fontSize: "14px", display: "flex", alignItems: "center", gap: "6px", transition: "all 200ms ease" }}><File size={16} /> Dokumenty</button>
        {canSeeHistory && (
          <button onClick={() => setActiveTab("historie")} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", backgroundColor: activeTab === "historie" ? "#E53935" : "#F5F5F5", color: activeTab === "historie" ? "#FFF" : "#666", cursor: "pointer", fontWeight: 500, fontSize: "14px", display: "flex", alignItems: "center", gap: "6px", transition: "all 200ms ease" }}><FileText size={16} /> Historie</button>
        )}
      </div>

      {activeTab === "info" && (
        <div style={{ 
          backgroundColor: "#FFF", 
          borderRadius: "12px", 
          border: "1px solid #E0E0E0", 
          padding: "24px"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#333", margin: 0, display: "flex", alignItems: "center", gap: "6px" }}><Package color="#E53935" size={20} /> Trenažéry ({machines.length})</h2>
            <button onClick={() => { setMachineForm({ model_type: DEFAULT_MACHINE_TYPE, contract_signed_at: today, status: DEFAULT_MACHINE_STATUS }); setShowMachineForm(true); }} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 16px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}><Plus size={18} /> Přidat</button>
          </div>
          {machines.length === 0 ? <p style={{ textAlign: "center", color: "#999", padding: "32px" }}>Zatím žádné trenažéry</p> : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {machines.map((m) => { const sc = statusColors[m.status] || statusColors.neobjednano; return (
                <Link key={m.id} to={"/machines/" + m.id} style={{ textDecoration: "none" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px", backgroundColor: "#F9F9F9", borderRadius: "10px", border: "1px solid #EEE" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Package color="#E53935" size={24} />
                      <div><p style={{ fontWeight: 600, color: "#333", margin: "0 0 4px" }}>Autom {m.model_type} : {m.serial_number}</p><span style={{ display: "inline-block", padding: "4px 8px", borderRadius: "4px", fontSize: "12px", backgroundColor: sc.bg, color: sc.color }}>{statusLabels[m.status] || "Neobjednáno"}</span></div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button onClick={(e) => handleDeleteMachine(e, m.id)} style={{ padding: "8px", borderRadius: "8px", border: "none", backgroundColor: "#FFEBEE", cursor: "pointer" }}><Trash2 size={16} color="#E53935" /></button>
                      <ChevronRight size={20} color="#999" />
                    </div>
                  </div>
                </Link>
              ); })}
            </div>
          )}
        </div>
      )}
      {activeTab === "info" && <CustomerSites customerId={id} />}

      {activeTab === "documents" && (
        <div style={{ 
          backgroundColor: "#FFF", 
          borderRadius: "12px", 
          border: "1px solid #E0E0E0", 
          padding: "24px"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#333", margin: 0, display: "flex", alignItems: "center", gap: "8px" }}><File color="#E53935" size={20} /> Dokumenty ({documents.length})</h2>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button onClick={() => setShowAiIntake(true)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "10px 14px", backgroundColor: "#F3E5F5", color: "#7B1FA2", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}><Sparkles size={16} /> AI příjem</button>
              <button onClick={() => { setCreateDocName(""); setCreateDocFiles([]); setShowCreateDocModal(true); }} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 16px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}><Plus size={18} /> Přidat dokument</button>
            </div>
          </div>
          {documents.length === 0 ? <p style={{ textAlign: "center", color: "#999", padding: "32px" }}>Zatím žádné dokumenty</p> : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {documents.map((doc) => (
                <div key={doc.id} style={{ padding: "14px 16px", backgroundColor: "#F9F9F9", borderRadius: "10px", border: "1px solid #EEE" }}>
                  {/* Document header */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
                      <FileText color="#E53935" size={22} style={{ flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontWeight: 600, color: "#333", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</p>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                      <button onClick={() => openDocEditModal(doc)} title="Upravit" style={{ padding: "8px", borderRadius: "8px", border: "none", backgroundColor: "#F5F5F5", cursor: "pointer" }}><Pencil size={16} color="#666" /></button>
                      <button onClick={() => openDeleteDocConfirm(doc)} title="Smazat dokument" style={{ padding: "8px", backgroundColor: "#FFEBEE", border: "none", cursor: "pointer", borderRadius: "8px" }}><Trash2 size={16} color="#E53935" /></button>
                    </div>
                  </div>

                  {/* Files list - inline, comma-separated */}
                  {(doc.files || []).length > 0 && (
                    <div style={{ marginTop: "4px", fontSize: "13px" }}>
                      {(doc.files || []).map((file, index) => (
                        <span key={file.id}>
                          {file.missing ? (
                            <span title="Soubor na serveru chybí – nahrajte jej znovu" style={{ color: "#F57C00", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                              <AlertTriangle size={14} /> {file.original_name} (chybí, nahrajte znovu)
                            </span>
                          ) : (
                          <a
                            href={`/api/customers/${id}/documents/${doc.id}/files/${file.id}/download`}
                            download={file.original_name}
                            style={{ color: "#0066cc", textDecoration: "underline", cursor: "pointer" }}
                          >
                            {file.original_name}
                          </a>
                          )}
                          {!file.missing && isPdfName(file.original_name) && (
                            <button
                              onClick={() => setPdfEditSource({ kind: "customer_file", customerId: Number(id), docId: doc.id, fileId: file.id, name: file.original_name })}
                              title="Upravit PDF pomocí AI"
                              aria-label={`Upravit ${file.original_name} pomocí AI`}
                              style={{ marginLeft: "6px", padding: "2px 6px", verticalAlign: "middle", background: "#F3E5F5", color: "#7B1FA2", border: "none", borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "12px" }}
                            >
                              <Wand2 size={13} /> AI
                            </button>
                          )}
                          {index < (doc.files || []).length - 1 && ", "}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "historie" && canSeeHistory && (
        <div style={{ 
          backgroundColor: "#FFF", 
          borderRadius: "12px", 
          border: "1px solid #E0E0E0", 
          padding: "24px"
        }}>
          <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#333", margin: "0 0 16px", display: "flex", alignItems: "center", gap: "8px" }}><FileText color="#E53935" size={20} /> Historie změn</h2>
          <HistoryTab customerId={id} />
        </div>
      )}

      <Modal show={showMachineForm} onClose={() => setShowMachineForm(false)} zIndex={9999}>
          <div style={{ backgroundColor: "#FFF", borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "400px", boxShadow: "0 20px 25px rgba(0,0,0,0.15)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}><h2 style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}>Nový trenažér</h2><button onClick={() => setShowMachineForm(false)} style={{ padding: "4px", background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button></div>
            <form onSubmit={handleAddMachine}>
              <div style={{ marginBottom: "16px" }}><label style={labelStyle}>Typ *</label><select value={machineForm.model_type} onChange={(e) => setMachineForm({ ...machineForm, model_type: e.target.value })} style={inputStyle}>{MACHINE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
              <div style={{ backgroundColor: "#E3F2FD", border: "1px solid #90CAF9", borderRadius: "8px", padding: "12px", marginBottom: "16px" }}><p style={{ color: "#1565C0", fontSize: "13px", margin: 0 }}>💡 Výrobní číslo se vygeneruje automaticky</p></div>
              <div style={{ marginBottom: "16px" }}><label style={labelStyle}>Status</label><select value={machineForm.status} onChange={(e) => setMachineForm({ ...machineForm, status: e.target.value })} style={inputStyle}>{Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
              <div style={{ marginBottom: "20px" }}><label style={labelStyle}>Datum podpisu smlouvy</label><input type="date" value={machineForm.contract_signed_at} onChange={(e) => setMachineForm({ ...machineForm, contract_signed_at: e.target.value })} style={inputStyle} /></div>
              <div style={{ display: "flex", gap: "12px" }}><button type="button" onClick={() => setShowMachineForm(false)} style={{ flex: 1, padding: "12px", border: "1px solid #E0E0E0", borderRadius: "8px", backgroundColor: "#FFF", cursor: "pointer", fontSize: "14px" }}>Zrušit</button><button type="submit" style={{ flex: 1, padding: "12px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>Přidat</button></div>
            </form>
          </div>
      </Modal>

      <Modal show={editing} onClose={() => setEditing(false)} zIndex={9999}>
          <div style={{ backgroundColor: "#FFF", borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "500px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 25px rgba(0,0,0,0.15)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}><h2 style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}>Upravit zákazníka</h2><button onClick={() => setEditing(false)} style={{ padding: "4px", background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button></div>
            <form onSubmit={handleUpdateCustomer}>
              <div style={{ marginBottom: "16px" }}><label style={labelStyle}>Název autoškoly *</label><input type="text" required value={customer.company_name || ""} onChange={(e) => setCustomer({ ...customer, company_name: e.target.value })} style={inputStyle} /></div>
              <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", marginBottom: "16px" }}>
                <div style={{ flex: 1 }}><label style={labelStyle}>IČO</label><input type="text" value={customer.ico || ""} onChange={(e) => setCustomer({ ...customer, ico: e.target.value })} style={inputStyle} placeholder="12345678" /></div>
                <button type="button" onClick={lookupAres} disabled={aresLoading} style={{ padding: "10px 14px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", height: "42px" }} title="Načíst z ARES">
                  {aresLoading ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
                </button>
                <div style={{ flex: 1 }}><label style={labelStyle}>DIČ</label><input type="text" value={customer.dic || ""} onChange={(e) => setCustomer({ ...customer, dic: e.target.value })} style={inputStyle} placeholder="CZ12345678" /></div>
              </div>
              {aresError && <p style={{ color: "#E53935", fontSize: "13px", margin: "-10px 0 16px" }}>{aresError}</p>}
              <div style={{ marginBottom: "16px" }}><label style={labelStyle}>Ulice</label><input type="text" value={customer.street || ""} onChange={(e) => setCustomer({ ...customer, street: e.target.value })} style={inputStyle} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}><div><label style={labelStyle}>Město</label><input type="text" value={customer.city || ""} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} style={inputStyle} /></div><div><label style={labelStyle}>PSČ</label><input type="text" value={customer.postal_code || ""} onChange={(e) => setCustomer({ ...customer, postal_code: e.target.value })} style={inputStyle} /></div></div>
              <div style={{ marginBottom: "16px" }}><label style={labelStyle}>Místo autoškoly</label><input type="text" value={customer.location || ""} onChange={(e) => setCustomer({ ...customer, location: e.target.value })} style={inputStyle} placeholder="např. Praha, Plzeň" /></div>
              <div style={{ marginBottom: "16px" }}><label style={labelStyle}>Zastupitel</label><input type="text" value={customer.proxy_name || ""} onChange={(e) => setCustomer({ ...customer, proxy_name: e.target.value })} style={inputStyle} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}><div><label style={labelStyle}>Telefon</label><input type="text" value={customer.phone || ""} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} style={inputStyle} /></div><div><label style={labelStyle}>Email</label><input type="email" value={customer.email || ""} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} style={inputStyle} /></div></div>
              <div style={{ marginBottom: "16px" }}>
                <ContactPersonList 
                  customerId={id}
                  onUpdate={() => fetchCustomer()}
                  onPendingContactsChange={(contacts) => { pendingContactsRef.current = contacts; }}
                />
              </div>
              <div style={{ marginBottom: "20px" }}><label style={labelStyle}>Poznámky</label><textarea value={customer.notes || ""} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} rows={3} style={{ ...inputStyle, resize: "vertical" }} /></div>
              <div style={{ display: "flex", gap: "12px" }}><button type="button" onClick={() => setEditing(false)} style={{ flex: 1, padding: "12px", border: "1px solid #E0E0E0", borderRadius: "8px", backgroundColor: "#FFF", cursor: "pointer", fontSize: "14px" }}>Zrušit</button><button type="submit" style={{ flex: 1, padding: "12px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>Uložit</button></div>
            </form>
          </div>
      </Modal>

      {/* Spin keyframe */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Create Document Modal (v2) */}
      {showCreateDocModal && createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000 }}>
          <div style={{ backgroundColor: "#FFF", borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "500px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 25px rgba(0,0,0,0.15)", margin: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}>Nový dokument</h2>
              <button onClick={() => { setShowCreateDocModal(false); setCreateDocName(""); setCreateDocFiles([]); }} style={{ padding: "4px", background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button>
            </div>

            {/* Document name */}
            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>Název dokumentu *</label>
              <input
                type="text"
                value={createDocName}
                onChange={(e) => setCreateDocName(e.target.value)}
                style={inputStyle}
                placeholder="Smlouva, Email, Faktury..."
                autoFocus
              />
            </div>

            {/* Selected files list */}
            {createDocFiles.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <p style={{ fontSize: "13px", fontWeight: 600, color: "#333", margin: "0 0 8px" }}>Soubory ({createDocFiles.length})</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "200px", overflowY: "auto" }}>
                  {createDocFiles.map(f => (
                    <div key={f.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", backgroundColor: "#F9F9F9", borderRadius: "8px", border: "1px solid #EEE" }}>
                      <File size={16} color="#E53935" style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: "13px", color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.file.name}</span>
                      <span style={{ fontSize: "11px", color: "#999", flexShrink: 0 }}>{(f.file.size / 1024).toFixed(0)} KB</span>
                      <button onClick={() => setCreateDocFiles(prev => prev.filter(x => x.id !== f.id))} style={{ padding: "4px", borderRadius: "4px", border: "none", backgroundColor: "#FFEBEE", cursor: "pointer", flexShrink: 0 }}><X size={12} color="#E53935" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload button */}
            <label style={{ width: "100%", padding: "10px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500, marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", boxSizing: "border-box" }}>
              <Upload size={16} /> Nahrát soubory
              <input type="file" multiple style={{ display: "none" }} onChange={(e) => handleCreateFileSelect(e.target.files)} />
            </label>

            {/* Actions */}
            <div style={{ display: "flex", gap: "12px" }}>
              <button type="button" onClick={() => { setShowCreateDocModal(false); setCreateDocName(""); setCreateDocFiles([]); }} style={{ flex: 1, padding: "12px", border: "1px solid #E0E0E0", borderRadius: "8px", backgroundColor: "#FFF", cursor: "pointer", fontSize: "14px" }}>Zrušit</button>
              <button onClick={handleCreateDocument} disabled={!createDocName.trim() || createUploading} style={{ flex: 1, padding: "12px", backgroundColor: createDocName.trim() ? "#E53935" : "#CCC", color: "#FFF", border: "none", borderRadius: "8px", cursor: createDocName.trim() ? "pointer" : "not-allowed", fontSize: "14px", fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                {createUploading ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Vytváří se...</> : "Vytvořit"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Document Modal (v2) */}
      {showDocEditModal && docToEdit && createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000 }}>
          <div style={{ backgroundColor: "#FFF", borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "480px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 25px rgba(0,0,0,0.15)", margin: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}>Upravit: {docToEdit.name}</h2>
              <button onClick={() => { setShowDocEditModal(false); setDocToEdit(null); setEditDocFiles([]); setEditUploadFiles([]); }} style={{ padding: "4px", background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button>
            </div>

            {/* 1. Document name */}
            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>Název dokumentu *</label>
              <input
                type="text"
                value={editDocName}
                onChange={(e) => setEditDocName(e.target.value)}
                style={inputStyle}
                autoFocus
              />
            </div>

            {/* 2. Existing files list */}
            <div style={{ marginBottom: "16px" }}>
              <p style={{ fontSize: "13px", fontWeight: 600, color: "#333", margin: "0 0 8px" }}>
                Soubory v tomto dokumentu ({editDocFiles.length})
              </p>
              {editDocFiles.length === 0 ? (
                <p style={{ fontSize: "13px", color: "#999", padding: "12px", textAlign: "center", backgroundColor: "#F9F9F9", borderRadius: "8px" }}>Žádné soubory</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "200px", overflowY: "auto" }}>
                  {editDocFiles.map(file => (
                    <div key={file.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", backgroundColor: "#FFF5F5", borderRadius: "8px", border: "1px solid #FFCDD2" }}>
                      <File size={16} color="#E53935" style={{ flexShrink: 0 }} />
                      <span
                        onClick={() => downloadFile(docToEdit.id, file.id, file.original_name)}
                        title="Klikni pro stažení"
                        style={{ flex: 1, fontSize: "13px", color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", textDecoration: "underline", textDecorationColor: "#CCC" }}
                      >{file.original_name}</span>
                      <span style={{ fontSize: "11px", color: "#999", flexShrink: 0 }}>{file.file_size ? (file.file_size / 1024).toFixed(0) + " KB" : ""}</span>
                      <button onClick={() => handleDeleteFile(docToEdit.id, file.id, file.original_name)} style={{ padding: "5px", borderRadius: "6px", border: "none", backgroundColor: "#FFEBEE", cursor: "pointer", flexShrink: 0 }} title="Smazat soubor"><Trash2 size={14} color="#E53935" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* New files to upload (staged) */}
            {editUploadFiles.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <p style={{ fontSize: "13px", fontWeight: 600, color: "#4CAF50", margin: "0 0 8px" }}>Připraveno k nahrání ({editUploadFiles.length})</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {editUploadFiles.map(f => (
                    <div key={f.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", backgroundColor: "#F1F8E9", borderRadius: "8px", border: "1px solid #C8E6C9" }}>
                      <File size={16} color="#4CAF50" style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: "13px", color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.file.name}</span>
                      <span style={{ fontSize: "11px", color: "#999", flexShrink: 0 }}>{(f.file.size / 1024).toFixed(0)} KB</span>
                      <button onClick={() => setEditUploadFiles(prev => prev.filter(x => x.id !== f.id))} style={{ padding: "4px", borderRadius: "4px", border: "none", backgroundColor: "#FFEBEE", cursor: "pointer", flexShrink: 0 }}><X size={12} color="#E53935" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 3. Upload button */}
            <label style={{ width: "100%", padding: "10px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500, marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", boxSizing: "border-box" }}>
              <Upload size={16} /> Nahrát soubory
              <input type="file" multiple style={{ display: "none" }} onChange={(e) => handleEditFileSelect(e.target.files)} />
            </label>

            {/* 4. Cancel / Save */}
            <div style={{ display: "flex", gap: "12px" }}>
              <button type="button" onClick={() => { setShowDocEditModal(false); setDocToEdit(null); setEditDocFiles([]); setEditUploadFiles([]); }} style={{ flex: 1, padding: "12px", border: "1px solid #E0E0E0", borderRadius: "8px", backgroundColor: "#FFF", cursor: "pointer", fontSize: "14px" }}>Zrušit</button>
              <button onClick={handleSaveDocEdit} disabled={!editDocName.trim() || editUploading} style={{ flex: 1, padding: "12px", backgroundColor: editDocName.trim() ? "#E53935" : "#CCC", color: "#FFF", border: "none", borderRadius: "8px", cursor: editDocName.trim() ? "pointer" : "not-allowed", fontSize: "14px", fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                {editUploading ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Ukládám...</> : "Uložit"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ConfirmModal show={!!confirmAction} title={confirmAction?.title} message={confirmAction?.message} confirmText="Smazat" cancelText="Zrušit" danger={confirmAction?.danger} onConfirm={confirmAction?.onConfirm} onCancel={() => setConfirmAction(null)} />

      <AiDocumentIntake
        open={showAiIntake}
        onClose={() => setShowAiIntake(false)}
        presetCustomerId={Number(id)}
        onSaved={() => { fetchDocuments(); fetchCustomer(); }}
      />
      <AiPdfEditor
        open={Boolean(pdfEditSource)}
        source={pdfEditSource}
        onClose={() => setPdfEditSource(null)}
        onSaved={() => fetchDocuments()}
      />

      {/* Copy Toast */}
      {copyToast && (
        <div style={{ position: "fixed", bottom: "20px", right: "20px", background: "#4CAF50", color: "#FFF", padding: "12px 20px", borderRadius: "8px", zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
          ✓ Zkopírováno
        </div>
      )}
    </div>
    </FadeIn>
  );
};

export default CustomerDetail;
